import { Markdown } from "@/components/markdown/Markdown.tsx";
import type { SavedObjectDetail } from "@/types/sources.ts";

/** Display names for the services we save objects from; others show their slug. */
const SERVICE_NAMES: Record<string, string> = { granola: "Granola", notion: "Notion" };

/** A saved object's service by its display name — "Granola" — or its slug when it has none. */
export function savedObjectServiceName(service: string): string {
  return SERVICE_NAMES[service] ?? service;
}

/**
 * An object saved from a connected service — meeting notes, a page — read as
 * the copy we hold, with a link back to the original.
 *
 * The body is someone else's text: anyone who can write in the user's Notion
 * or join their meeting can put markup in it. So it renders as markdown with no
 * raw HTML — tags show as text — and its links only navigate. The link back is
 * offered only when it is http(s), whatever the server let through.
 */
export const SavedObjectView = ({ detail }: { detail: SavedObjectDetail }) => {
  const service = savedObjectServiceName(detail.service);
  const saved = new Date(detail.savedAtMs).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  const safeLink = /^https?:\/\//i.test(detail.link) ? detail.link : undefined;
  return (
    <article className="flex flex-col gap-3 px-4 py-4">
      <header className="flex flex-col gap-1">
        <h2 className="text-base font-medium">{detail.title}</h2>
        <p className="text-xs text-muted-foreground">
          From {service} · saved {saved}
          {safeLink && (
            <>
              {" · "}
              <a
                href={safeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="underline-offset-2 hover:underline"
              >
                Open in {service}
              </a>
            </>
          )}
        </p>
      </header>
      <Markdown className="prose prose-zinc max-w-full text-sm" untrusted>
        {detail.content}
      </Markdown>
      {detail.truncated && (
        <p className="text-xs text-muted-foreground">
          This is the start of the {service} copy; open it in {service} for the rest.
        </p>
      )}
    </article>
  );
};
